/**
 * OrderProgress — Whatnot-style order progress tracker.
 *
 * - `variant="compact"` (default) renders a horizontal 3-node strip with
 *   pulsing current node, ideal for the Orders list rows.
 * - `variant="detail"` renders a richer vertical timeline with timestamps
 *   and an inline tracking-link CTA for the buyer.
 *
 * Handles both physical orders (Paid → Shipped → Delivered) and digital
 * orders (Paid → Ready to download). Refunded / cancelled orders show a
 * single muted node so the row isn't visually noisy.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircleIcon,
  CreditCardIcon,
  TruckIcon,
  PackageIcon,
  DownloadSimpleIcon,
  XCircleIcon,
} from "@phosphor-icons/react";

const BLUE = "#1D78AB";

function ts(t) {
  if (!t) return null;
  try {
    return new Date(t).toLocaleString();
  } catch {
    return null;
  }
}

function etaLabel(order) {
  // Build "Arrives by Fri Jun 4" (single ETA) or
  // "Arrives Mon Jun 2 – Fri Jun 6" (window). Uses the eta_max_at value
  // the backend stores at ship time. Returns null if no ETA was set.
  const lo = order.eta_min_at;
  const hi = order.eta_max_at;
  if (!hi) return null;
  const opts = { weekday: "short", month: "short", day: "numeric" };
  try {
    const hiD = new Date(hi).toLocaleDateString(undefined, opts);
    if (!lo || lo === hi) return `Arrives by ${hiD}`;
    const loD = new Date(lo).toLocaleDateString(undefined, opts);
    return `Arrives ${loD} – ${hiD}`;
  } catch {
    return null;
  }
}

function stepsForPhysical(order) {
  const paidStatuses = ["paid", "shipped", "delivered"];
  const isPaid = paidStatuses.includes(order.status);
  const ship = order.shipping_status;
  const isShipped = ship === "shipped" || ship === "delivered";
  const isDelivered = ship === "delivered";
  return [
    {
      key: "paid",
      label: "Paid",
      icon: CreditCardIcon,
      at: order.created_at,
      done: isPaid,
    },
    {
      key: "shipped",
      label: isShipped ? "Shipped" : "Packing",
      icon: TruckIcon,
      at: order.shipped_at,
      done: isShipped,
    },
    {
      key: "delivered",
      label: "Delivered",
      icon: CheckCircleIcon,
      at: order.delivered_at,
      done: isDelivered,
    },
  ];
}

function stepsForDigital(order) {
  const paidStatuses = ["paid", "shipped", "delivered"];
  const isPaid = paidStatuses.includes(order.status);
  // Use the dedicated delivered_at when the backend stamped it (auto-
  // delivery sets it on payment finalization). Fall back to created_at
  // so legacy rows still render a timestamp under the second step.
  const deliveredAt = order.delivered_at || order.created_at;
  return [
    {
      key: "paid",
      label: "Paid",
      icon: CreditCardIcon,
      at: order.created_at,
      done: isPaid,
    },
    {
      key: "auto_delivered",
      label: "Auto-delivered",
      icon: DownloadSimpleIcon,
      at: deliveredAt,
      done: isPaid,
      note: "Download unlocked the moment payment cleared.",
      badge: "AUTO",
    },
  ];
}

function terminalState(order) {
  if (order.status === "refunded") {
    return { label: "Refunded", color: "text-zinc-500", icon: XCircleIcon };
  }
  if (order.status === "cancelled") {
    return { label: "Cancelled", color: "text-zinc-500", icon: XCircleIcon };
  }
  return null;
}

export default function OrderProgress({ order, variant = "compact" }) {
  const term = terminalState(order);
  if (term) {
    const Icon = term.icon;
    return (
      <div
        className={`flex items-center gap-2 text-xs ${term.color}`}
        data-testid={`order-progress-${order.id}`}
      >
        <Icon size={16} weight="fill" />
        <span className="font-bold uppercase tracking-widest">{term.label}</span>
      </div>
    );
  }

  const isDigital = !!order.is_digital;
  const steps = isDigital ? stepsForDigital(order) : stepsForPhysical(order);
  // The "current" step is the first not-yet-done step — that's the one we
  // pulse to draw the buyer's eye. If everything is done, last step is current.
  const currentIdx = steps.findIndex((s) => !s.done);
  const current = currentIdx === -1 ? steps.length - 1 : currentIdx;

  if (variant === "detail") {
    return (
      <div
        className="border border-zinc-200 bg-white p-6 rounded-xl"
        data-testid={`order-progress-detail-${order.id}`}
      >
        <p className="overline text-zinc-500 mb-5">PROGRESS</p>
        <ol className="space-y-5">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isCurrent = i === current && !s.done;
            const next = steps[i + 1];
            return (
              <li key={s.key} className="flex items-start gap-3 relative">
                {next && (
                  <span
                    className={`absolute left-[11px] top-7 bottom-[-20px] w-[2px] ${
                      s.done ? "bg-[#1D78AB]" : "bg-zinc-200"
                    }`}
                    aria-hidden
                  />
                )}
                <div
                  className={`relative h-6 w-6 rounded-full grid place-items-center flex-shrink-0 z-10 ${
                    s.done
                      ? "bg-[#1D78AB] text-white"
                      : isCurrent
                      ? "bg-white border-2 border-[#1D78AB] text-[#1D78AB]"
                      : "bg-zinc-100 text-zinc-400 border border-zinc-200"
                  }`}
                >
                  {isCurrent && (
                    <span
                      className="absolute inset-0 rounded-full animate-ping bg-[#1D78AB]/30"
                      aria-hidden
                    />
                  )}
                  <Icon size={13} weight={s.done || isCurrent ? "fill" : "regular"} />
                </div>
                <div className="flex-1 min-w-0 -mt-0.5">
                  <div
                    className={`font-semibold text-sm ${
                      s.done || isCurrent ? "text-zinc-900" : "text-zinc-400"
                    } flex items-center gap-2 flex-wrap`}
                  >
                    <span>{s.label}</span>
                    {s.badge && s.done && (
                      <span
                        className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5"
                        data-testid={`order-progress-badge-${s.key}`}
                      >
                        {s.badge}
                      </span>
                    )}
                  </div>
                  {s.at && s.done && (
                    <div className="text-xs text-zinc-500" data-testid={`order-progress-ts-${s.key}`}>
                      {ts(s.at)}
                    </div>
                  )}
                  {s.note && s.done && (
                    <div
                      className="text-xs text-zinc-500 mt-0.5"
                      data-testid={`order-progress-note-${s.key}`}
                    >
                      {s.note}
                    </div>
                  )}
                  {s.key === "shipped" && s.done && order.shipping_status === "shipped" && etaLabel(order) && (
                    <div
                      className="inline-flex items-center gap-1 rounded-full bg-[#1D78AB]/10 border border-[#1D78AB]/30 text-[#1D78AB] text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 mt-1.5"
                      data-testid={`order-progress-eta-${order.id}`}
                    >
                      <TruckIcon size={11} weight="fill" />
                      {etaLabel(order)}
                    </div>
                  )}
                  {isCurrent && s.key === "shipped" && order.shipping_status === "pending" && (
                    <div className="text-xs text-zinc-500">
                      Seller is preparing your order.
                    </div>
                  )}
                  {isCurrent && s.key === "delivered" && order.tracking_url && (
                    <a
                      href={order.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1D78AB] hover:underline mt-1"
                      data-testid={`order-progress-track-${order.id}`}
                    >
                      <TruckIcon size={12} weight="fill" />
                      Track on {order.carrier} →
                    </a>
                  )}
                  {s.key === "auto_delivered" && s.done && (
                    <Link
                      to="/dashboard?tab=library"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1D78AB] hover:underline mt-1.5"
                      data-testid={`order-progress-download-${order.id}`}
                    >
                      <DownloadSimpleIcon size={12} weight="fill" />
                      Open in your Library →
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    );
  }

  // compact variant
  const eta = etaLabel(order);
  return (
    <div className="w-full" data-testid={`order-progress-${order.id}`}>
      <div
        className="flex items-center gap-1.5"
        aria-label={`Progress: step ${current + 1} of ${steps.length}`}
      >
      {steps.map((s, i) => {
        const Icon = s.icon;
        const isCurrent = i === current && !s.done;
        return (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center gap-1 min-w-0">
              <div
                className={`relative h-5 w-5 rounded-full grid place-items-center flex-shrink-0 ${
                  s.done
                    ? "bg-[#1D78AB] text-white"
                    : isCurrent
                    ? "bg-white border-2 border-[#1D78AB] text-[#1D78AB]"
                    : "bg-zinc-100 text-zinc-400 border border-zinc-200"
                }`}
              >
                {isCurrent && (
                  <span
                    className="absolute inset-0 rounded-full animate-ping bg-[#1D78AB]/30"
                    aria-hidden
                  />
                )}
                <Icon size={10} weight={s.done || isCurrent ? "fill" : "regular"} />
              </div>
              <span
                className={`hidden sm:block text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${
                  s.done || isCurrent ? "text-zinc-700" : "text-zinc-400"
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-[2px] mt-[-12px] sm:mt-[-14px] ${
                  steps[i + 1].done || (steps[i].done && isCurrent === false)
                    ? "bg-[#1D78AB]"
                    : "bg-zinc-200"
                }`}
                style={{ background: steps[i + 1].done ? BLUE : undefined }}
              />
            )}
          </React.Fragment>
        );
      })}
      </div>
      {eta && order.shipping_status === "shipped" && (
        <div
          className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#1D78AB]/10 border border-[#1D78AB]/30 text-[#1D78AB] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
          data-testid={`order-progress-eta-${order.id}`}
        >
          <TruckIcon size={10} weight="fill" />
          {eta}
        </div>
      )}
    </div>
  );
}
